import Button from '@atoms/Button/Button';
import Icon from '@atoms/Icon/Icon';
import useVideo from '@hooks/useVideo';
import MuxPlayer, { MuxPlayerRefAttributes } from '@mux/mux-player-react';
import clsx from 'clsx';
import { useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import styles from './Video.module.scss';

interface Props {
  video: {
    playbackId: string;
  };
  autoplay?: boolean;
  loop?: boolean;
  controls?: boolean;
  alt?: string;
  thumbnail?: {
    url?: string;
  };
  className?: string | false;
  showCustomControls?: boolean;
}

export default function MuxVideo({
  video,
  autoplay = false,
  loop = false,
  controls = true,
  alt = '',
  thumbnail,
  className,
  showCustomControls = false,
}: Props) {
  const videoRef = useRef<MuxPlayerRefAttributes>(null);
  const { ref: containerRef, inView } = useInView();
  const [isMuted, setIsMuted] = useState<boolean>(autoplay || false);

  const { isVideoPlaying, play, pause } = useVideo({ reference: videoRef });

  const handleClick = () => {
    if (isVideoPlaying) {
      pause();
    } else {
      play();
    }
  };

  const handleToggleMute = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setIsMuted(state => !state);
  };

  const replayVideo = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    if (!videoRef.current) return;

    videoRef.current.currentTime = 0;
  };

  useEffect(() => {
    if (!autoplay) return;

    if (inView) {
      play();
    } else {
      pause();
    }
  }, [autoplay, play, pause, inView]);

  return (
    <div
      ref={containerRef}
      className={clsx(
        styles.mediaContainer,
        styles.muxContainer,
        isVideoPlaying && styles.isPlaying,
        className,
      )}
      onClick={handleClick}
    >
      <MuxPlayer
        playbackId={video.playbackId}
        ref={videoRef}
        className={clsx(
          styles.media,
          (!isVideoPlaying || autoplay) && styles.withoutControls,
        )}
        title={alt}
        muted={isMuted}
        loop={loop}
        poster={thumbnail?.url}
        style={{
          // @ts-expect-error - MuxPlayer variable
          '--controls': controls ? undefined : 'none',
        }}
      />
      {((!isVideoPlaying && !autoplay) || !controls) && (
        <div className={styles.buttonContainer} onClick={handleClick}>
          {!isVideoPlaying && (
            <Button
              icon="PLAY"
              className={styles.button}
              onClick={handleClick}
              label="Play video"
              hideLabel
              variant="black"
            />
          )}
        </div>
      )}

      <div className={styles.pause}>
        <Icon icon="PAUSE_SOLID" className={styles.pauseIcon} strokeWidth={1} />
      </div>

      {showCustomControls && (
        <>
          <button
            onClick={handleToggleMute}
            className={clsx(styles.controls, styles.muteButton)}
          >
            <Icon
              icon={isMuted ? 'SOUND_OFF' : 'SOUND_HIGH'}
              className={styles.controlsIcon}
              strokeWidth={2}
            />
          </button>

          <button
            onClick={replayVideo}
            className={clsx(styles.controls, styles.restartButton)}
          >
            <Icon
              icon="RESTART"
              className={styles.controlsIcon}
              strokeWidth={2}
            />
          </button>
        </>
      )}
    </div>
  );
}
